import * as Types from '@aeppic/types'
import { isReference } from './is.js'
import { Form } from './form.js'
import { QueryScopes } from './query/fluent.js'
import { Query } from './index.js'

export function getAllResolveQuerySteps(query: string): [string, number][] {
  let steps = query.split('->').map(step => step.trim())
  let enumerated: [string, number][] = steps.filter(step => step.length > 0).map((step, i) => [step, i])
  return enumerated
}

/**
 * Resolves a document path starting from a root document by traversing nested properties based on the provided query string.
 * 
 * @param {Types.IdOrReference} root - The root document ID or reference from which to start resolving the path.
 * @param {string} query - The query string representing the path to resolve (e.g., 'data.a -> data.b -> data.c').
 * @param {Object} options - An object containing options for resolving the document path.
 * @param {Function} options.getDocument - A function that retrieves a document based on its ID or reference.
 * @param {Function} [options.warn] - An optional function for logging warnings or messages during the resolution process.
 * 
 * @returns {Promise<Types.Document>} The resolved document at the end of the path, or null if any step in the path resolution fails.
 */
export async function resolveDocumentPath(root: Types.IdOrReference, query: string, options: {
  get: (id: Types.IdOrReference) => Promise<Types.Document>,
  getAll: (ids: Types.IdOrReference[]) => Promise<Types.Document[]>,
  resolveAll(start: Types.IdOrReference, path: string): Promise<Types.Document[]>,
  findOne(query: Query.Query): Promise<Types.Document>
  find: (query: Query.Query, options?: { size?: number }) => Promise<Types.Document[]>
  getFormForDocument: (id: Types.IdOrReference) => Promise<Form>,
  warn?: (...args: any[]) => void
}): Promise<Types.Document[]> {
  if (typeof query !== 'string') {
    throw new Error('Query must be a string (e.g data.a -> data.b -> data.c)')
  }

  let documentsToStartFrom: Types.Document[] = [await options.get(root)]

  let steps = getAllResolveQuerySteps(query)

  for (const [step, i] of steps) {
    // Is it a related document ?
    const firstCharacterIsUppercase = step[0].toUpperCase() === step[0]
    const documentsForNextStep: Types.Document[] = []

    while (documentsToStartFrom.length > 0) {
      const current = documentsToStartFrom.shift()

      if (!current) {
        continue
      }

      if (firstCharacterIsUppercase) {
        // Find relation on current document form
        const form = await options.getFormForDocument(current)
        const relations = await options.getAll(form.data.relatedDocuments as Types.Reference[])
        const relation = await relations.find(relation => relation.data.name === step)

        if (!relation) {
          options.warn?.('Relation', step, 'not found on at step', i, 'of', steps, 'for document', current.id)
          return null
        }

        switch (relation.data.type) {
          case 'RESOLVE': {
            const next = await options.resolveAll(current.id, relation.data.resolve as string)
            next && documentsForNextStep.push(...next)
            break
          }
          case 'STATIC': {
            const next = await options.get(relation.data.reference as Types.Reference)
            documentsForNextStep.push(next)
            break
          }
          case 'ANCESTOR_FORM': {
            let matchingAncestor = null

            for (let i = current.a_forms.length - 1; i >= 0; i--) {
              const formId = current.a_forms[i]

              for (const formRefsToLookFor of relation.data.ancestorForms as Types.Reference[]) {
                if (formId === formRefsToLookFor.id) {
                  matchingAncestor = current.a[i]
                  break
                }
              }

              if (matchingAncestor) {
                break
              }
            }

            if (matchingAncestor) {
              const next = await options.get(matchingAncestor)
              next && documentsForNextStep.push(next)
            } else {
              options.warn?.('Ancestor form not found on at step', i, 'of', steps, 'for document', current.id)
              continue
            }

            break
          }
          case 'QUERY': {
            const definedScope = relation.data.queryScope
            const scopes = new QueryScopes(current.id)

            const scope = definedScope === 'CHILDREN' ? scopes.children()
              : definedScope === 'DESCENDANTS' ? scopes.descendants()
                : definedScope === 'GLOBAL' ? scopes.global()
                  : null

            if (!scope) {
              options.warn?.('Unknown scope', definedScope, 'found on at step', i, 'of', steps)
              return null
            }

            const fn = getCompiledFunction(relation.data.query as string, { identifyingDocument: current, identifyingField: 'query' })

            if (!fn) {
              return null
            }

            const size = relation.data.multiple ? 1_000 : 1
            const results = await options.find(fn(scope), { size })

            documentsForNextStep.push(...results)

            break
          }
          default: {
            options.warn?.('Unknown relation type', relation.data.type, 'found on at step', i, 'of', steps)
            return null
          }
        }

      } else {
        // Move through the nested values of this document 
        let value: any = lookupDocumentPath(current, step, options)

        if (!Array.isArray(value)) {
          value = [value]
        }

        for (const fieldValue of value) {
          // If the value is null, continue to the next item
          if (fieldValue == null) {
            continue
          }

          // Ensure current is a Types.Reference
          if (typeof fieldValue !== 'string' && !isReference(fieldValue)) {
            options.warn?.('Expected a string or reference at ', step, ', for ', current.id, 'at step', i, 'of', steps)
            break
          }

          // Get the document from Aeppic.get
          const next = await options.get(fieldValue)
          next && documentsForNextStep.push(next)
        }
      }
    }

    documentsToStartFrom = documentsForNextStep
  }

  return documentsToStartFrom.filter(Boolean)
}

/**
 * Resolves a document path starting from a root document by traversing nested properties based on the provided query string.
 * 
 * This is a synchronous version. All callbacks must be synchronous.
 * 
 * @param {Types.IdOrReference} root - The root document ID or reference from which to start resolving the path.
 * @param {string} query - The query string representing the path to resolve (e.g., 'data.a -> data.b -> data.c').
 * @param {Object} options - An object containing synchronous options for resolving the document path.
 * @param {Function} options.get - Synchronously retrieves a document based on its ID or reference.
 * @param {Function} options.getAll - Synchronously retrieves multiple documents by ID or reference.
 * @param {Function} options.resolve - Synchronously resolves a document path starting from a given ID.
 * @param {Function} options.findOne - Synchronously finds a single document based on a query.
 * @param {Function} [options.find] - Synchronously finds all documents based on a query.
 * @param {Function} options.getFormForDocument - Synchronously retrieves the form definition for a given document.
 * @param {Function} [options.warn] - Optional logging function for warnings.
 * 
 * @returns {Types.Document[]} The resolved documents at the end of the path, or null if any step in the path resolution fails.
 */
export function resolveDocumentPathSync(
  root: Types.IdOrReference,
  query: string,
  options: {
    get: (id: Types.IdOrReference) => Types.Document,
    getAll: (ids: Types.IdOrReference[]) => Types.Document[],
    resolve: (start: Types.IdOrReference, path: string) => Types.Document,
    find: (query: Query.Query, options?: { size?: number }) => Types.Document[],
    getFormForDocument: (id: Types.IdOrReference) => Form,
    warn?: (...args: any[]) => void
  }
): Types.Document | Types.Document[] {
  if (typeof query !== 'string') {
    throw new Error('Query must be a string (e.g data.a -> data.b -> data.c)');
  }

  let documentsToStartFrom: Types.Document[] = [options.get(root)];
  let steps = getAllResolveQuerySteps(query);

  for (const [step, i] of steps) {
    const firstCharacterIsUppercase = step[0].toUpperCase() === step[0];
    const documentsForNextStep: Types.Document[] = [];

    while (documentsToStartFrom.length > 0) {
      const current = documentsToStartFrom.shift();
      if (!current) continue;

      if (firstCharacterIsUppercase) {
        const form = options.getFormForDocument(current);
        const relations = options.getAll(form.data.relatedDocuments as Types.Reference[]);
        const relation = relations.find(rel => rel.data.name === step);

        if (!relation) {
          options.warn?.('Relation', step, 'not found at step', i, 'of', steps, 'for document', current.id);
          return null;
        }

        if (relation.data.multiple) {
          options.warn?.('Relation', step, 'cannot be used to find single document (multiple is checked) at step', i, 'of', steps);
          return null;
        }

        switch (relation.data.type) {
          case 'RESOLVE': {
            const next = options.resolve(current.id, relation.data.resolve as string);
            documentsForNextStep.push(next);
            break;
          }
          case 'STATIC': {
            const next = options.get(relation.data.reference as Types.Reference);
            documentsForNextStep.push(next);
            break;
          }
          case 'ANCESTOR_FORM': {
            let matchingAncestor = null;

            for (let idx = current.a_forms.length - 1; idx >= 0; idx--) {
              const formId = current.a_forms[idx];
              for (const formRefsToLookFor of relation.data.ancestorForms as Types.Reference[]) {
                if (formId === formRefsToLookFor.id) {
                  matchingAncestor = current.a[idx];
                  break;
                }
              }
              if (matchingAncestor) break;
            }

            if (matchingAncestor) {
              const next = options.get(matchingAncestor);
              documentsForNextStep.push(next);
            } else {
              options.warn?.('Ancestor form not found at step', i, 'of', steps, 'for document', current.id);
              continue;
            }
            break;
          }
          case 'QUERY_ALL':
          case 'QUERY': {
            const definedScope = relation.data.queryScope;
            const scopes = new QueryScopes(current.id);

            const scope =
              definedScope === 'CHILDREN'
                ? scopes.children()
                : definedScope === 'DESCENDANTS'
                ? scopes.descendants()
                : definedScope === 'GLOBAL'
                ? scopes.global()
                : null;

            if (!scope) {
              options.warn?.('Unknown scope', definedScope, 'found at step', i, 'of', steps);
              return null;
            }

            const fn = getCompiledFunction(relation.data.query as string, {
              identifyingDocument: current,
              identifyingField: 'query'
            });
            if (!fn) return null;

            const size = relation.data.type === 'QUERY_ALL' ? 1_000 : 1;
            const results = options.find(fn(scope), { size });
            documentsForNextStep.push(...results);
            break;
          }
          default: {
            options.warn?.('Unknown relation type', relation.data.type, 'found at step', i, 'of', steps);
            return null;
          }
        }
      } else {
        let value: any = lookupDocumentPath(current, step, options);
        if (!Array.isArray(value)) value = [value];

        for (const fieldValue of value) {
          if (fieldValue == null) continue;
          if (typeof fieldValue !== 'string' && !isReference(fieldValue)) {
            options.warn?.('Expected a string or reference at', step, ', for', current.id, 'at step', i, 'of', steps);
            break;
          }
          const next = options.get(fieldValue);
          documentsForNextStep.push(next);
        }
      }
    }

    documentsToStartFrom = documentsForNextStep;
  }

  return documentsToStartFrom.filter(Boolean)
}

const COMPILED_FUNCTIONS = new Map<string, Function>()

function getCompiledFunction(query: string, { identifyingDocument, identifyingField }: { identifyingDocument: Types.Document, identifyingField: string }) {
  const key = `${identifyingDocument.id}@${identifyingDocument.v}:${query}}`
  if (COMPILED_FUNCTIONS.has(key)) {
    return COMPILED_FUNCTIONS.get(key)
  }

  const identifier = `${identifyingDocument.id}@${identifyingDocument.v} -> ${identifyingField}`
  const functionBody = `return scope.${query.trim()}`
  const fullDefinition = `// INFO: ${identifyingDocument.data.name} Field: ${identifyingField}\n\n${functionBody}\n//# sourceURL=${identifier}`

  try {
    const fn = new Function('scope', fullDefinition) as any
    fn.displayName = `RelationResolveFunction_${identifier}`
    COMPILED_FUNCTIONS.set(key, fn)
    return fn as Function
  } catch (compileError) {
    // tslint:disable-next-line no-console
    console.error('Error preparing dynamic code', compileError, fullDefinition)
    COMPILED_FUNCTIONS.set(key, null)
    return null
  }
}

function lookupDocumentPath(document: Types.Document, path: string, { warn }: { warn?: (...args: any[]) => void }) {
  let value: any = document

  // Split the current step into parts
  let parts = path.split('.')

  for (let j = 0; j < parts.length; j++) {
    if (value === null) {
      warn?.("Expected a value but got 'null'")
      return null
    }

    if (typeof value !== 'object') {
      warn?.("Expected an object but got a", typeof value)
      return null
    }

    // Get the next part of the step
    let part = parts[j]

    // If the document is null or the part is not in the root, return null
    if (!(part in value)) {
      warn?.('Part', part, 'not in', value)
      return null
    }

    // Update the current document
    value = value[part]
  }

  return value
}

